import React from "react";
import { Phone, MapPin, Clock, MessageCircle, Mail, ArrowRight, Navigation } from "lucide-react";
import { Link } from "react-router-dom";
import { BRANCHES, INSTITUTE } from "@/lib/content";
import useSEO from "@/hooks/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact Kothari Classes Jalgaon | Baliram Peth & Yogeshwar Nagar Branches",
    description: "Visit Kothari Classes at Baliram Peth or Yogeshwar Nagar, Jalgaon. Call +91 74994 76736 or WhatsApp for admission inquiries, directions and free demo classes.",
    canonical: "https://www.kothariclasses.in/contact",
  });
  const waHref = `https://wa.me/${INSTITUTE.whatsapp_number}?text=${encodeURIComponent(INSTITUTE.whatsapp_message)}`;

  return (
    <div data-testid="contact-page">
      <section className="bg-gray-950 text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">Contact</span>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1]">
              Visit us. Call us.<br/>Or just WhatsApp.
            </h1>
            <p className="mt-5 text-gray-400 text-lg">Two branches in Jalgaon — choose the one closest to you.</p>
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="bg-white border-b border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-4">
          <a href={`tel:${INSTITUTE.phone_raw}`} className="border border-gray-200 p-5 flex items-center gap-4 card-lift" data-testid="contact-call-card">
            <div className="w-11 h-11 bg-red-50 border border-red-200 flex items-center justify-center"><Phone className="w-5 h-5 text-red-600"/></div>
            <div>
              <div className="eyebrow">Call</div>
              <div className="font-display font-bold text-gray-950">{INSTITUTE.phone}</div>
            </div>
          </a>
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="border border-gray-200 p-5 flex items-center gap-4 card-lift" data-testid="contact-whatsapp-card">
            <div className="w-11 h-11 bg-red-50 border border-red-200 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-red-600"/></div>
            <div>
              <div className="eyebrow">WhatsApp</div>
              <div className="font-display font-bold text-gray-950">Chat with us instantly</div>
            </div>
          </a>
          <a href={`mailto:${INSTITUTE.email}`} className="border border-gray-200 p-5 flex items-center gap-4 card-lift" data-testid="contact-email-card">
            <div className="w-11 h-11 bg-red-50 border border-red-200 flex items-center justify-center"><Mail className="w-5 h-5 text-red-600"/></div>
            <div>
              <div className="eyebrow">Email</div>
              <div className="font-display font-bold text-gray-950 break-all">{INSTITUTE.email}</div>
            </div>
          </a>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="border border-gray-200 p-5 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="all-phones-row">
            <span className="eyebrow">All Lines</span>
            {INSTITUTE.phones.map((p) => (
              <a key={p.raw} href={`tel:${p.raw}`} className="flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-red-600" data-testid={`all-phone-${p.raw}`}>
                <Phone className="w-3.5 h-3.5 text-red-600" />
                <span>{p.number}</span>
                <span className="text-[10px] uppercase tracking-widest text-gray-500">· {p.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Branches */}
      <section className="bg-gray-50 py-16 lg:py-24" data-testid="branches-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {BRANCHES.map((b, i) => (
            <div key={b.id} className={`grid lg:grid-cols-12 gap-6 items-stretch ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`branch-${b.id}`}>
              <div className="lg:col-span-5 bg-white border border-gray-200 p-8 flex flex-col justify-between">
                <div>
                  <span className="eyebrow">{b.short}</span>
                  <h2 className="font-display text-3xl font-black text-gray-950 mt-2">{b.name}</h2>
                  <div className="space-y-3 mt-6 text-sm">
                    <div className="flex gap-3"><MapPin className="w-4 h-4 text-red-600 mt-0.5 shrink-0"/><span className="text-gray-800">{b.address}</span></div>
                    <div className="flex gap-3"><Phone className="w-4 h-4 text-red-600 mt-0.5 shrink-0"/><a className="text-gray-800 hover:text-red-600" href={`tel:${b.phone_raw}`}>{b.phone}</a></div>
                    <div className="flex gap-3"><Clock className="w-4 h-4 text-red-600 mt-0.5 shrink-0"/><span className="text-gray-800">{b.hours}</span></div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-6 border-t border-gray-200 mt-6">
                  <a href={`tel:${b.phone_raw}`} className="btn-primary rounded-none text-sm" data-testid={`branch-call-${b.id}`}><Phone className="w-4 h-4"/>Call Branch</a>
                  <a href={`https://wa.me/${b.whatsapp_raw}?text=${encodeURIComponent(INSTITUTE.whatsapp_message)}`} target="_blank" rel="noopener noreferrer" className="btn-secondary rounded-none text-sm" data-testid={`branch-whatsapp-${b.id}`}><MessageCircle className="w-4 h-4"/>WhatsApp</a>
                  <a
                    href={b.directions}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`branch-directions-${b.id}`}
                    className="group inline-flex items-center gap-2 rounded-none border border-gray-950 bg-gray-950 text-white text-sm font-semibold px-4 py-3 transition-all duration-200 hover:bg-red-600 hover:border-red-600 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(220,38,38,0.6)]"
                  >
                    <Navigation className="w-4 h-4 transition-transform group-hover:rotate-12" />
                    Get Directions
                  </a>
                </div>
              </div>
              <div className="lg:col-span-7 border border-gray-200 overflow-hidden min-h-[320px]">
                <iframe
                  title={`map-${b.id}`}
                  src={b.map}
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: 320 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid={`branch-map-${b.id}`}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-red-600 text-white py-16" data-testid="contact-cta-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-6">
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tight text-center lg:text-left">Still deciding? Book a free demo.</h2>
          <Link to="/admissions" className="bg-white text-red-600 font-semibold px-6 py-3.5 hover:bg-gray-100 transition-colors inline-flex items-center gap-2" data-testid="contact-cta-demo">
            Book Free Demo <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </section>
    </div>
  );
}
